import clsx from 'clsx';
import { components } from '@mosey/api-types';
import { CheckIcon, XIcon } from '@heroicons/react/solid';
import {
  TruncatedList,
  TruncatedListProps,
} from '@mosey/components/layout/TruncatedList';
import { TextLink } from '@mosey/components/navigation/TextLink';
import { BaseCard } from '@mosey/components/layout/BaseCard';
import { QuestionMark } from '@mosey/components/Icons';
import {
  useTasksOverviewData,
  useTasksOverviewLocationId,
} from '../../utils/hooks';
import { generateResolverTasksBrowserUrl } from '../../utils';
import { ResolverType } from '../../utils/types';
import { ModuleHeading } from './ModuleHeading';

type TaskRef = components['schemas']['TaskRef'];

const dateRegex =
  /(?:\b|\D)((?:\d{1,2}[/\-.]\d{1,2}[/\-.]\d{2,4})|(?:\d{2,4}[/\-.]\d{1,2}[/\-.]\d{1,2})|\b(?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[a-z]*[\s,]\s*\d{1,2}(?:st|nd|rd|th)?[\s,]\s*\d{4}|\b\d{1,2}\s*(?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[a-z]*\s*\d{4})(?:\b|\D)/gi;

const isNoishAnswer = (task: TaskRef) => {
  if (task.source.type === 'question') {
    const { answer } = task.source;

    if (
      answer === 'false' ||
      answer === '0' ||
      answer === null ||
      answer === undefined
    ) {
      return true;
    }
  }

  return false;
};

export const ConfigurationCollection = () => {
  const { reviewQuestionTasks = [] } = useTasksOverviewData();
  const locationId = useTasksOverviewLocationId();

  if (locationId && reviewQuestionTasks.length > 0) {
    let showAmount: TruncatedListProps['amount'] = Math.min(
      reviewQuestionTasks.filter((task) => !isNoishAnswer(task)).length,
      3,
    );

    if (showAmount === 0) {
      showAmount = undefined;
    }

    return (
      <TruncatedList
        amount={showAmount}
        isStacked
        as="nav"
        aria-live="polite"
        title={
          <ModuleHeading
            secondaryContent={
              <TextLink
                to={generateResolverTasksBrowserUrl({
                  locationId,
                  resolverType: ResolverType.Review,
                })}
              >
                Edit
              </TextLink>
            }
          >
            Configuration
          </ModuleHeading>
        }
      >
        {reviewQuestionTasks
          .toSorted((a, b) => {
            if (a.source.type !== 'question') {
              return 1;
            }

            if (b.source.type !== 'question') {
              return -1;
            }

            if (
              (isNoishAnswer(a) && isNoishAnswer(b)) ||
              (!isNoishAnswer(a) && !isNoishAnswer(b))
            ) {
              return 0;
            }

            if (isNoishAnswer(a) && !isNoishAnswer(b)) {
              return 1;
            }

            return -1;
          })
          .map((task) => {
            const { id, title, source } = task;

            if (source.type === 'question') {
              const { answer } = source;
              const isDate = answer ? dateRegex.test(answer) : false;

              return (
                <li key={id} className="group/card-component">
                  <BaseCard
                    title={
                      <div
                        className={clsx(isNoishAnswer(task) && 'text-zinc-600')}
                      >
                        {title}
                      </div>
                    }
                    size="medium"
                    variant="nav"
                    to={generateResolverTasksBrowserUrl({
                      locationId,
                      resolverType: ResolverType.Review,
                      taskId: id,
                    })}
                    rightIcon={
                      answer === 'true' || isDate ? (
                        <CheckIcon
                          className="text-zinc-700"
                          aria-label={isDate ? `(${answer})` : '(Yes)'}
                          aria-hidden="false"
                        />
                      ) : answer === 'false' || answer === '0' ? (
                        <XIcon
                          className="text-zinc-600"
                          aria-label="(No)"
                          aria-hidden="false"
                        />
                      ) : answer === null || answer === undefined ? (
                        <QuestionMark
                          className="text-zinc-600"
                          aria-label="(Not answered)"
                        />
                      ) : (
                        <div
                          className="text-sm font-medium text-zinc-700"
                          aria-label={`(${answer})`}
                        >
                          {answer}
                        </div>
                      )
                    }
                  />
                </li>
              );
            }

            return null;
          })}
      </TruncatedList>
    );
  }

  return null;
};
